import React, { useEffect, useState } from 'react';
import Icon from 'react-eva-icons';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormFeedback,
  Row,
  Spinner,
} from 'reactstrap';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useCookies } from 'react-cookie';
import { setAuthTokens, get, showErrors } from '@/utils/helpers';
import { clientFetch } from '@/utils/service';
import { fetchProfile } from '@/store/auth/authActions';
import logoDark from '@/assets/images/logo-dark.svg';
import logoLight from '@/assets/images/logo-light.svg';
import AuthCarousel from '@/views/AuthCarousel';
import Page from '@/views/Page';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { t } = useTranslation();
  const { layoutMode } = useSelector((state) => ({
    layoutMode: state.Layout.layoutMode,
  }));

  const [passwordType, setPasswordType] = useState('password');
  const togglePasswordType = () => {
    setPasswordType((val) => (val === 'password' ? 'text' : 'password'));
  };

  // TODO refactor this
  useEffect(() => {
    if (layoutMode === 'dark') {
      document.body.classList.remove('bg-transparent');
    } else {
      document.body.className = 'bg-transparent';
    }
    // remove classname when component will unmount
    return function cleanup() {
      document.body.className = '';
    };
  });

  const history = useHistory()
  const dispatch = useDispatch();
  const [cookies, setCookie] = useCookies();
  const onSubmit = async (values, actions) => {
    try {
      const res = await clientFetch('/users/token', {
        method: 'POST',
        body: JSON.stringify(values),
      });
      if (res?.access) {
        setAuthTokens({
          access: res?.access,
          refresh: res?.refresh,
          setCookie,
        });

        setTimeout(() => {
          dispatch(fetchProfile()).then((result) => {
            history.push(!!result.is_superuser ? '/psychologists' : '/consultations');
          });
        }, 50);
      }
    } catch (error) {
      showErrors(error, actions);
    }
  };

  return (
    <Page metaTitle="Авторизація">
      <div className="auth-page">
        <Container fluid className="p-0">
          <Row className="g-0 align-items-center">
            <Col className="col-xxl-4 col-lg-5 col-md-6">
              <Row className="justify-content-center g-0">
                <Col xl={10}>
                  <div className="p-4">
                    <Card className="mb-0">
                      <CardBody>
                        <div className="auth-full-page-content rounded d-flex p-3 my-2">
                          <div className="w-100">
                            <div className="d-flex flex-column h-100">
                              <div className="mb-4 mb-md-5">
                                <Link to="/" className="d-block auth-logo">
                                  <img
                                    src={logoDark}
                                    alt=""
                                    height="22"
                                    className="auth-logo-dark me-start"
                                  />
                                  <img
                                    src={logoLight}
                                    alt=""
                                    height="22"
                                    className="auth-logo-light me-start"
                                  />
                                </Link>
                              </div>

                              <div className="auth-content my-auto">
                                <div className="text-center">
                                  <h5 className="mb-0">{t('login.title')}</h5>
                                </div>
                                <Formik
                                  initialValues={{
                                    username: '',
                                    password: '',
                                  }}
                                  onSubmit={onSubmit}
                                  validationSchema={Yup.object({
                                    username: Yup.string().required(
                                      t('validation.required')
                                    ),
                                    password: Yup.string().required(
                                      t('validation.required')
                                    ),
                                  })}
                                >
                                  {({ errors, isSubmitting }) => (
                                    <Form className="mt-4 pt-2 needs-validation">
                                      <Field name="username">
                                        {({ field, meta }) => (
                                          <div className="field mb-4">
                                            <div className="form-floating form-floating-custom">
                                              <input
                                                type="text"
                                                className={classNames(
                                                  'form-control',
                                                  {
                                                    'is-invalid':
                                                      meta.touched &&
                                                      !!meta?.error,
                                                  }
                                                )}
                                                placeholder={t('login.username')}
                                                {...field}
                                              />
                                              <label>
                                                {t('login.username')}
                                              </label>
                                              <div className="form-floating-icon">
                                                <Icon
                                                  name="people-outline"
                                                  fill="#555b6d"
                                                />
                                              </div>
                                            </div>
                                            {meta.touched && !!meta?.error && (
                                              <FormFeedback type="invalid">
                                                {meta?.error}
                                              </FormFeedback>
                                            )}
                                          </div>
                                        )}
                                      </Field>
                                      <Field name="password">
                                        {({ field, meta }) => (
                                          <div className="field mb-4">
                                            <div className="form-floating form-floating-custom auth-pass-inputgroup">
                                              <input
                                                type={passwordType}
                                                className={classNames(
                                                  'form-control form-control--password pe-5',
                                                  {
                                                    'is-invalid':
                                                      meta.touched &&
                                                      !!meta?.error,
                                                  }
                                                )}
                                                placeholder={t('login.password')}
                                                {...field}
                                              />

                                              <button
                                                type="button"
                                                className="btn btn-link btn-password-addon position-absolute text-muted h-100 end-0 top-0"
                                                onClick={togglePasswordType}
                                              >
                                                <i
                                                  className={`far ${
                                                    passwordType === 'password'
                                                      ? 'fa-eye'
                                                      : 'fa-eye-slash'
                                                  }`}
                                                />
                                              </button>
                                              <label>
                                                {t('login.password')}
                                              </label>
                                              <div className="form-floating-icon">
                                                <Icon
                                                  name="lock-outline"
                                                  fill="#555b6d"
                                                />
                                              </div>
                                            </div>
                                            {meta.touched && !!meta?.error && (
                                              <FormFeedback type="invalid">
                                                {meta?.error}
                                              </FormFeedback>
                                            )}
                                          </div>
                                        )}
                                      </Field>

                                      <div className="mb-3">
                                        <Button
                                          color="primary"
                                          className="w-100 waves-effect waves-light"
                                          type="submit"
                                          disabled={isSubmitting}
                                        >
                                          {isSubmitting && (
                                            <Spinner size="sm" />
                                          )}
                                          <span> {t('login.enter')}</span>
                                        </Button>
                                      </div>
                                      {(!!get(errors, ['details']) || !!get(errors, ['detail'])) && (
                                        <div className="form-error">
                                          {get(errors, ['details']) || get(errors, ['detail'])}
                                        </div>
                                      )}
                                    </Form>
                                  )}
                                </Formik>

                                {/* <div className="mt-4 pt-3 text-center">
                                  <Link
                                    to="/reset-password"
                                    className="text-primary fw-semibold"
                                  >
                                    {t('login.forgotPassword')}
                                  </Link>
                                </div> */}
                              </div>

                              <div className="mt-4 text-center">
                                <p className="mb-0">
                                  © {new Date().getFullYear()} HoldYou
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </Col>
              </Row>
            </Col>
            <AuthCarousel colSize="col-xxl-8 col-lg-7 col-md-6" />
          </Row>
        </Container>
      </div>
    </Page>
  );
};

export default Login;
